import { shareApi as api } from './shareApi';
export const addTagTypes = ['Shares'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      updateShare: build.mutation<UpdateShareApiResponse, UpdateShareApiArg>({
        query: queryArg => ({
          url: `/share/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateShare,
          params: { type: queryArg['type'] }
        }),
        invalidatesTags: ['Shares']
      }),
      deleteShare: build.mutation<DeleteShareApiResponse, DeleteShareApiArg>({
        query: queryArg => ({
          url: `/share/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg['delete'] }
        }),
        invalidatesTags: ['Shares']
      }),
      getSharedPeopleById: build.query<
        GetSharedPeopleByIdApiResponse,
        GetSharedPeopleByIdApiArg
      >({
        query: queryArg => ({
          url: `/sharedPeople/${queryArg.id}`,
          params: {
            type: queryArg['type'],
            ...(queryArg.name ? { name: queryArg.name } : {})
          }
        }),
        providesTags: ['Shares']
      }),
      getUnsharedPeopleById: build.query<
        GetUnsharedPeopleByIdApiResponse,
        GetUnsharedPeopleByIdApiArg
      >({
        query: queryArg => ({
          url: `/unSharedPeople/${queryArg.id}`,
          params: { type: queryArg['type'], name: queryArg.name }
        }),
        providesTags: ['Shares']
      }),
      getSharedAsset: build.mutation<
        GetSharedAssetApiResponse,
        GetSharedAssetApiArg
      >({
        query: queryArg => ({
          url: `/sharedAsset`,
          method: 'POST',
          body: queryArg.sharedAssetBody
        }),
        invalidatesTags: ['Shares']
      }),
      addToShare: build.mutation<AddToShareApiResponse, AddToShareApiArg>({
        query: queryArg => ({
          url: `/addToShare`,
          method: 'POST',
          body: queryArg.addToShare
        }),
        invalidatesTags: ['Shares']
      }),
      bulkUpdateShare: build.mutation<
        BulkUpdateShareApiResponse,
        BulkUpdateShareApiArg
      >({
        query: queryArg => ({
          url: `/bulk-update`,
          method: 'POST',
          body: queryArg.bulkUpdateShare
        }),
        invalidatesTags: ['Shares']
      }),
      getSharePermissionsById: build.query<
        GetSharePermissionsByIdApiResponse,
        GetSharePermissionsByIdApiArg
      >({
        query: queryArg => ({
          url: `/sharePermissions/${queryArg.id}`,
          params: { type: queryArg['type'] }
        }),
        providesTags: ['Shares']
      }),
      unshareBulk: build.mutation<UnshareBulkApiResponse, UnshareBulkApiArg>({
        query: queryArg => ({
          url: `/unshareBulk`,
          method: 'POST',
          body: queryArg.bulkUnshare
        }),
        invalidatesTags: ['Shares']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type UpdateShareApiResponse =
  /** status 200 The Share's details has been updated successfully */ {
    ok?: boolean;
    data?: Share;
    message?: string;
  };
export type UpdateShareApiArg = {
  /** ID of the Share to update */
  id: string;
  /** Type of the Share to update */
  type: string;
  /** Share that needs to be added to the application */
  updateShare: UpdateShare;
};
export type DeleteShareApiResponse =
  /** status 200 The Share's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteShareApiArg = {
  /** ID of the Share to delete */
  id: string;
  /** flag to hard delete */
  delete?: string;
};
export type GetSharedPeopleByIdApiResponse =
  /** status 200 Shared people details found. */ {
    ok?: boolean;
    data?: SharedPeople;
    message?: string;
  };
export type GetSharedPeopleByIdApiArg = {
  id: string;
  /** Type of shared people to retrieve */
  type: string;
  /** Optional search text to filter people */
  name?: string;
};
export type GetUnsharedPeopleByIdApiResponse =
  /** status 200 Shared people details found. */ {
    ok?: boolean;
    data?: UnsharedPeople;
    message?: string;
  };
export type GetUnsharedPeopleByIdApiArg = {
  id: string;
  /** Asset ID to filter the Share */
  name: string;
  /** Type of unshared people to retrieve */
  type: string;
};
export type GetSharedAssetApiResponse =
  /** status 201 Share has been created successfully. */ {
    ok?: boolean;
    data?: SharedAssetResponse;
    message?: string;
  };
export type GetSharedAssetApiArg = {
  /** Share that needs to be added to the application */
  sharedAssetBody: SharedAssetBody;
};
export type AddToShareApiResponse =
  /** status 201 Share has been created successfully. */ {
    ok?: boolean;
    data?: BulkUpdateShareResponse;
    message?: string;
  };
export type AddToShareApiArg = {
  /** Share that needs to be added to the application */
  addToShare: AddToShare;
};
export type BulkUpdateShareApiResponse =
  /** status 201 Share has been created successfully. */ {
    ok?: boolean;
    data?: BulkUpdateShareResponse;
    message?: string;
  };
export type BulkUpdateShareApiArg = {
  /** Share that needs to be updated in the application */
  bulkUpdateShare: BulkUpdateShare;
};
export type GetSharePermissionsByIdApiResponse =
  /** status 200 Share permissions found. */ {
    ok?: boolean;
    data?: SharePermissions;
    message?: string;
  };
export type GetSharePermissionsByIdApiArg = {
  /** Share ID to filter the Share */
  id: string;
  /** Type of the Share to filter the Share */
  type: string;
};
export type UnshareBulkApiResponse =
  /** status 200 Share permissions found. */ {
    ok?: boolean;
    data?: BulkUnshareResponse;
    message?: string;
  };
export type UnshareBulkApiArg = {
  /** Share ID to filter the Share */
  id: string;
  /** Share that needs to be updated in the application */
  bulkUnshare: BulkUnshare;
};
export type Share = {
  count?: number;
  rows?: {
    id: string;
    status: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
  }[];
};
export type UpdateShare = {
  peopleId?: string[];
};
export type SharedPeople = {
  id?: string;
  profileImage?: string;
  name?: string;
  type?: string;
}[];
export type UnsharedPeople = {
  count?: number;
  rows?: {
    id?: string;
    profileImage?: string;
    name?: string;
  };
};
export type SharedAssetResponse = {
  count?: number;
  rows?: {
    id?: string;
    url?: string;
    thumbNailUrl?: string;
    shareId?: string;
  }[];
};
export type SharedAssetBody = {};
export type BulkUpdateShareResponse = {
  success?: boolean;
};
export type AddToShare = {
  id?: string[];
  peopleId?: string[] | { id: string; roleId?: string }[];
  type?: string;
};
export type BulkUpdateShare = {
  emails?: {
    peopleId?: string;
    email?: string;
  }[];
};
export type SharePermissions = string[];
export type BulkUnshareResponse = {
  success?: boolean;
};
export type BulkUnshare = {
  assetIds?: string[];
  peopleIds?: string[];
  recipeIds?: string[];
  memoryId?: string[];
  isGroup?: boolean;
  groupIds?: string[];
  subscriberId?: string;
};
export const {
  useUpdateShareMutation,
  useDeleteShareMutation,
  useGetSharedPeopleByIdQuery,
  useGetUnsharedPeopleByIdQuery,
  useGetSharedAssetMutation,
  useAddToShareMutation,
  useBulkUpdateShareMutation,
  useGetSharePermissionsByIdQuery,
  useUnshareBulkMutation
} = injectedRtkApi;
